<script>
import DashboardLayout from "@/Layouts/DashboardLayout.vue";

export default {
    layout: DashboardLayout,
};
</script>

<script setup>
import { computed, ref } from "vue";
import { Head, useForm, usePage } from "@inertiajs/vue3";
import { isEmpty, isNull, omitBy } from "lodash";
import BrandButton from "@/Components/Brand/BrandButton.vue";
import BrandInput from "@/Components/Brand/BrandInput.vue";
import BrandFriendCard from "@/Components/Friends/BrandFriendCard.vue";
import FriendsHero from "@/Components/Friends/FriendsHero.vue";
import FriendsNav from "@/Components/Friends/FriendsNav.vue";
import InertiaPagination from "@/Components/Pagination/InertiaPagination.vue";

const form = useForm({
    first_name: null,
    last_name: null,
    organization: null,
    searchBy: "name",
    username: null,
});

const isNewSearch = ref(false);
const showErrors = ref(false);

const searchByOptions = [
    { value: "name", label: "Name" },
    { value: "username", label: "Username" },
    { value: "organization", label: "Organization" },
];

const users = computed(() => usePage().props.users);
const hasSearchResults = computed(() => users.value?.data?.length > 0);
const totalFound = computed(() => users.value?.meta?.total ?? 0);

const isFormValid = computed(() => {
    switch (form.searchBy) {
        case "name":
            return (
                form.first_name?.length > 0 &&
                form.last_name?.length > 0 &&
                !form.processing
            );
        case "username":
            return form.username?.length > 0 && !form.processing;
        case "organization":
            return form.organization?.length > 0 && !form.processing;
    }
    return false;
});

const handleSearchByChange = () => {
    isNewSearch.value = true;
    showErrors.value = false;

    ["first_name", "last_name", "organization", "username"].forEach(
        (key) => (form[key] = null),
    );
    form.defaults();
    form.reset("first_name", "last_name", "organization", "username");
};

function submitForm() {
    isNewSearch.value = false;

    form.transform((data) => {
        return omitBy(data, isNull);
    }).post(route("friends.search"), {
        preserveScroll: true,
        preserveState: true,
        replace: true,
        onSuccess: () => {
            showErrors.value = !hasSearchResults.value;
        },
    });
}
</script>

<template>
    <Head :title="`Find a Friend's Wish List`" />

    <FriendsHero
        title="Find Friends"
        subtitle="Find friends and invite them to share lists."
    />

    <div class="md:grid md:grid-cols-[352px_1fr] md:gap-16">
        <!-- Desktop sidebar overlaps the hero band; mobile renders tabs -->
        <div class="md:-mt-[137px]">
            <FriendsNav />
        </div>

        <div class="mt-6 min-w-0 md:mt-[61px]">
            <!-- Search form -->
            <form @submit.prevent="submitForm">
                <div
                    class="grid gap-5 md:gap-6"
                    :class="
                        form.searchBy === 'name'
                            ? 'md:grid-cols-3'
                            : 'md:grid-cols-2'
                    "
                >
                    <div>
                        <label
                            for="searchBy"
                            class="mb-0.5 block font-brand text-[15px] font-bold leading-[19px] text-[#3c3938]"
                            >Search by&hellip;</label
                        >
                        <div class="relative">
                            <select
                                id="searchBy"
                                v-model="form.searchBy"
                                name="searchBy"
                                class="h-11 w-full appearance-none rounded-brand border border-[#a79e9b] bg-white bg-none px-3 pr-9 font-brand text-[15px] text-[#3c3938] focus:border-brand-navy focus:ring-1 focus:ring-brand-navy"
                                @change="handleSearchByChange"
                            >
                                <option
                                    v-for="option in searchByOptions"
                                    :key="option.value"
                                    :value="option.value"
                                >
                                    {{ option.label }}
                                </option>
                            </select>
                            <svg
                                viewBox="0 0 14 12"
                                class="pointer-events-none absolute right-3 top-1/2 h-2.5 w-3 -translate-y-1/2 text-brand-red"
                                fill="currentColor"
                                stroke="currentColor"
                                stroke-width="2.5"
                                stroke-linejoin="round"
                                aria-hidden="true"
                            >
                                <path d="M3 3 L11 3 L7 9.5 Z" />
                            </svg>
                        </div>
                    </div>

                    <template v-if="form.searchBy === 'name'">
                        <div>
                            <label
                                for="firstName"
                                class="mb-0.5 block font-brand text-[15px] font-bold leading-[19px] text-[#3c3938]"
                                >First Name</label
                            >
                            <BrandInput
                                id="firstName"
                                v-model="form.first_name"
                                type="search"
                                name="first_name"
                                autocomplete="off"
                                autocorrect="off"
                                autocapitalize="off"
                                spellcheck="false"
                                autofocus
                            />
                        </div>
                        <div>
                            <label
                                for="lastName"
                                class="mb-0.5 block font-brand text-[15px] font-bold leading-[19px] text-[#3c3938]"
                                >Last Name</label
                            >
                            <BrandInput
                                id="lastName"
                                v-model="form.last_name"
                                type="search"
                                name="last_name"
                                autocomplete="off"
                                autocorrect="off"
                                autocapitalize="off"
                                spellcheck="false"
                            />
                        </div>
                    </template>

                    <div v-else-if="form.searchBy === 'username'">
                        <label
                            for="username"
                            class="mb-0.5 block font-brand text-[15px] font-bold leading-[19px] text-[#3c3938]"
                            >Username</label
                        >
                        <BrandInput
                            id="username"
                            v-model="form.username"
                            type="search"
                            name="username"
                            autocomplete="off"
                            autocorrect="off"
                            autocapitalize="off"
                            spellcheck="false"
                            autofocus
                        />
                    </div>

                    <div v-else-if="form.searchBy === 'organization'">
                        <label
                            for="organization"
                            class="mb-0.5 block font-brand text-[15px] font-bold leading-[19px] text-[#3c3938]"
                            >Organization</label
                        >
                        <BrandInput
                            id="organization"
                            v-model="form.organization"
                            type="search"
                            name="organization"
                            autocomplete="off"
                            autocorrect="off"
                            autocapitalize="off"
                            spellcheck="false"
                            autofocus
                        />
                    </div>
                </div>

                <div class="mt-6 flex justify-end md:mt-8">
                    <BrandButton
                        type="submit"
                        class="h-11 w-full text-[15px] md:w-[307px]"
                        :class="{
                            'pointer-events-none opacity-60':
                                !isFormValid && !form.processing,
                        }"
                        :disabled="!isFormValid || form.processing"
                        :loading="form.processing"
                    >
                        {{
                            form.processing ? "Searching" : "Search for Friend"
                        }}
                    </BrandButton>
                </div>
            </form>

            <!-- No results -->
            <p
                v-if="showErrors"
                class="mt-8 rounded-brand bg-brand-cream px-5 py-4 font-brand text-[15px] text-brand-navy"
            >
                Sorry, we couldn&rsquo;t find a match for you. Please try again,
                or invite the person you&rsquo;re looking for to Gift Hero!
            </p>

            <!-- Results -->
            <section
                v-if="hasSearchResults && !isNewSearch"
                class="mt-10 md:mt-14"
            >
                <div class="flex items-baseline justify-between gap-4">
                    <h2
                        class="font-brand text-[24px] font-bold text-brand-navy md:text-[26px]"
                    >
                        Search Results
                    </h2>
                    <p class="font-brand text-[14px] text-[#565a77]">
                        {{ totalFound }}
                        {{ totalFound === 1 ? "User" : "Users" }} Found
                    </p>
                </div>

                <div
                    class="mt-5 grid grid-cols-1 gap-6 sm:grid-cols-2 xl:grid-cols-3"
                >
                    <BrandFriendCard
                        v-for="user in users.data"
                        :key="user.uuid"
                        :user="user"
                    />
                </div>

                <!-- Badge legend -->
                <div
                    class="mt-6 flex items-center gap-10 font-brand text-[12px] font-medium text-[#102a3d]"
                >
                    <span class="flex items-center gap-2.5">
                        <span
                            class="grid size-[30px] place-items-center rounded-[2px] bg-brand-navy"
                        >
                            <img
                                :src="
                                    asset('images/friends/badge-top-hero.png')
                                "
                                alt=""
                                class="size-[15px]"
                            />
                        </span>
                        Top Gift Hero
                    </span>
                    <span class="flex items-center gap-2.5">
                        <span
                            class="grid size-[30px] place-items-center rounded-[2px] border border-brand-tan bg-white"
                        >
                            <img
                                :src="asset('images/brand/logo-mark.png')"
                                alt=""
                                class="size-[15px] object-contain"
                            />
                        </span>
                        New to Gift Hero
                    </span>
                </div>

                <InertiaPagination
                    v-if="
                        !isEmpty(users?.meta) &&
                        !isEmpty(users?.links) &&
                        users.meta.last_page > 1
                    "
                    :meta="users.meta"
                    :links="users.links"
                    class="mt-8"
                />
            </section>
        </div>
    </div>
</template>
